<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type {
  IFilterDetailsModel,
  IFilterDetailsProps,
  IFilterSizeStep
} from '@/types/composables/filter-shared/useFilterDetails';
import { computed } from 'vue';
import useFoldersService from '@/composables/apiServices/useFoldersService';
import FilterAndOrSwitch from './FilterAndOrSwitch.vue';

const { t } = useI18n();
const query = defineModel<IFilterDetailsModel>({ required: true });
const fromToAndOr = defineModel<boolean>('fromToAndOr', { default: false });
const mailboxModel = defineModel<string | null>('mailbox', { default: null });
const props = defineProps<IFilterDetailsProps>();

const { getFlatFolders } = useFoldersService();
const { data: folderList } = getFlatFolders();
const computedFolderList = computed(
  () =>
    folderList.value?.map((folder) => ({ title: folder.fullName, value: folder.fullName })) ?? []
);

const sizeList: IFilterSizeStep[] = ['B', 'KB', 'MB'];
const directionList = computed(() => [
  { title: t('settings.filters.form.buttons.off'), value: 'off' },
  { title: t('settings.filters.form.buttons.bigger'), value: 'larger' },
  { title: t('settings.filters.form.buttons.smaller'), value: 'smaller' }
]);
</script>

<template>
  <v-row class="align-start ga-2">
    <VTextField
      id="filter-from-input"
      v-model="query.from.value"
      :disabled="props.isPending"
      :error-messages="props.errors['query.from']"
      :label="t('settings.filters.form.from')"
      bg-color="false"
      clearable
      data-test-id="settings-filter-from-input"
      name="from"
      variant="outlined"
    />
    <FilterAndOrSwitch
      v-if="props.isSearchView"
      v-model="fromToAndOr"
      :is-pending="props.isPending"
    />
    <VTextField
      id="filter-to-input"
      v-model="query.to.value"
      :disabled="props.isPending"
      :error-messages="props.errors['query.to']"
      :label="t('settings.filters.form.to')"
      bg-color="false"
      clearable
      data-test-id="settings-filter-to-input"
      name="to"
      variant="outlined"
    />
  </v-row>

  <VTextField
    id="filter-subject-input"
    v-model="query.subject.value"
    :disabled="props.isPending"
    :error-messages="props.errors['query.subject']"
    :label="t('settings.filters.form.subject')"
    bg-color="false"
    clearable
    data-test-id="settings-filter-subject-input"
    name="subject"
    variant="outlined"
  />

  <VTextField
    id="filter-contains-input"
    v-model="query.text.value"
    :disabled="props.isPending"
    :error-messages="props.errors['query.text']"
    :label="t('settings.filters.form.contains.label')"
    bg-color="false"
    clearable
    data-test-id="settings-filter-contains-input"
    name="contains"
    variant="outlined"
  >
    <!-- <template #append-inner>
      <v-tooltip
        :text="t('settings.filters.form.contains.info')"
        location="end"
        origin="auto"
        width="240"
      >
        <template #activator="{ props: tooltipActivatorProps }">
          <v-icon
            class="ml-2 cursor-pointer"
            color="bimi-verified"
            icon="mdi-help-circle-outline"
            size="20"
            v-bind="tooltipActivatorProps"
            sentry-unmask
          />
        </template>
      </v-tooltip>
    </template> -->
  </VTextField>

  <VTextField
    id="filter-listId-input"
    v-model="query.listId.value"
    :disabled="props.isPending"
    :error-messages="props.errors['query.listId']"
    :label="t('settings.filters.form.listId.label')"
    bg-color="false"
    clearable
    data-test-id="settings-filter-listId-input"
    name="listID"
    variant="outlined"
  >
    <template #append-inner>
      <v-tooltip
        :text="t('settings.filters.form.listId.info')"
        :disabled="false"
        location="end"
        :open-on-click="true"
        :open-on-hover="true"
        origin="auto"
        width="240"
      >
        <template #activator="{ props: tooltipActivatorProps }">
          <v-icon
            class="ml-2 cursor-pointer"
            color="bimi-verified"
            icon="mdi-help-circle-outline"
            size="20"
            v-bind="tooltipActivatorProps"
            sentry-unmask
          />
        </template>
      </v-tooltip>
    </template>
  </VTextField>

  <VAutocomplete
    v-if="props.isSearchView"
    v-model="mailboxModel"
    :disabled="props.isPending"
    :items="computedFolderList"
    :label="t('settings.filters.form.folder')"
    :list-props="{ density: 'compact' }"
    bg-color="false"
    clearable
    data-test-id="settings-filter-mailbox-input"
    variant="outlined"
  />

  <div class="size-grid">
    <v-select
      v-model="query.size.direction.value"
      :disabled="props.isPending"
      :items="directionList"
      data-test-id="settings-filter-size-select"
      variant="outlined"
      sentry-unmask
    />

    <v-text-field
      v-model="query.size.value.value"
      :disabled="query.size.direction.value === 'off' || props.isPending"
      :error-messages="props.errors['query.size.value']"
      bg-color="false"
      clearable
      data-test-id="settings-filter-number-input"
      inputmode="numeric"
      label="0"
      pattern="\d"
      single-line
      variant="outlined"
    />

    <v-select
      v-model="query.size.step.value"
      :disabled="query.size.direction.value === 'off' || props.isPending"
      :items="sizeList"
      data-test-id="settings-filter-size-select"
      variant="outlined"
    />
  </div>

  <v-checkbox-btn
    v-model="query.ha.value"
    :disabled="props.isPending"
    :label="t('settings.filters.form.attachment')"
    color="primary"
    hide-details
    size="40"
    sentry-unmask
  />
</template>

<style lang="scss" scoped>
@use '../../../assets/styles/settings' as settings;
@use 'sass:map';

.filters__buttons {
  > button {
    flex: 1;
  }

  &-active {
    background-color: rgb(var(--v-theme-secondary-container));
  }
}

.size-grid {
  display: grid;
  grid-template-columns: 1fr 0.5fr 0.5fr;
  gap: 1rem;
  align-items: flex-start;

  @media screen and #{map.get(settings.$display-breakpoints, 'md-and-down')} {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'field1 field1'
      'field2 field3';
    gap: 0;

    & > :nth-child(1) {
      grid-area: field1;
    }

    & > :nth-child(2) {
      grid-area: field2;
      margin-right: 0.5rem;
    }

    & > :nth-child(3) {
      grid-area: field3;
      margin-left: 0.5rem;
    }
  }
}
</style>
